import type { ButtonProps, MantineTheme } from '@mantine/core'
import clsx from 'clsx'

/**
 * Root layout for StepCare-styled buttons (matches legacy UI Button).
 * Filled CTAs use `.interactive-fill*` utilities — label color comes from
 * `--sc-interactive-fg` / `--sc-interactive-primary-fg`, not hardcoded white.
 */
const BUTTON_LAYOUT =
  'flex cursor-pointer p-2 px-3 py-1 align-middle font-light'

const BUTTON_VARIANT_ROOT: Record<string, string> = {
  primary:
    'interactive-fill-primary data-[disabled=true]:bg-gray-400!',
  danger:
    'bg-red-500 hover:bg-red-400 text-white data-[disabled=true]:bg-red-200!',
  warning:
    'bg-orange-500! hover:bg-orange-400! text-white data-[disabled=true]:bg-orange-200!',
  /** Mantine’s default `filled` — same chrome as StepCare `default` */
  filled:
    'interactive-fill hover:opacity-95 data-[disabled=true]:bg-gray!',
  default:
    'interactive-fill hover:opacity-90 data-[disabled]:bg-gray-400! data-[active=true]:opacity-95',
  light: 'interactive-subtle shadow-sm text-inherit',
  subtle: 'interactive-subtle text-inherit',
  outline:
    'border border-outline-chrome bg-transparent hover:bg-shade ',
  transparent:
    '!bg-none hover:bg-shade bg-transparent! shadow-none drop-shadow-none text-inherit! hover:bg-(--sc-interactive-bg-hover)/10!  ',
  /** Shell nav tab — idle (semi-transparent on chrome) */
  nav: 'interactive-nav shadow-sm',
  /** Shell nav tab — selected (brighter pill + brand label); use like legacy primary/light split */
  navActive: 'interactive-nav-active shadow-sm font-medium'
}

const compactSizeCls = (size: ButtonProps['size']): string =>
  size === 'xs' || size === 'sm' ? 'p-1 px-2' : ''

/**
 * Tailwind classes for Button root — used by Mantine theme so StepCare buttons
 * (including modals) share the same look as legacy `@packages/ui` Forms/Button.
 *
 * Omitted `variant` is treated as `default` (primary CTA). `filled` matches
 * Mantine’s default Button variant. Unmapped variants (e.g. `white`, `gradient`)
 * get layout + white text unless extended above.
 */
export function stepcareButtonRootClassNames(
  _theme: MantineTheme,
  props: ButtonProps
): string {
  const variantKey =
    props.variant === undefined || props.variant === null
      ? 'default'
      : String(props.variant)

  const variantCls = BUTTON_VARIANT_ROOT[variantKey]
  const sizeCls = compactSizeCls(props.size)
  const base = BUTTON_LAYOUT

  if (variantCls !== undefined) {
    return clsx(base, variantCls, sizeCls)
  }

  return clsx(BUTTON_LAYOUT, sizeCls)
}
